import {
  Col,
  DatePicker,
  Divider,
  Form,
  Modal,
  Row,
  Switch,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLogout } from '../../../api'
import { usePrintStatementReport } from '../../../api/profile'
import { COLOR_STATUS_USER, STATUS_USER_ENUM } from '../../../App.constants'
import copyIcon from '../../../common/images/png/Copy.png'
import { ReactComponent as CalenderIcon } from '../../../common/images/svg/CalendarIcon.svg'
import { ReactComponent as CloseIcon } from '../../../common/images/svg/CloseIcon.svg'
import { ReactComponent as SignoutIcon } from '../../../common/images/svg/SignOut.svg'
import { ReactComponent as SignoutPasswordIcon } from '../../../common/images/svg/SignOutPassword.svg'
import { ReactComponent as StatementIcon } from '../../../common/images/svg/Statement.svg'
import { ReactComponent as SuccessIcon } from '../../../common/images/svg/Success.svg'
import { ReactComponent as UserCircleIcon } from '../../../common/images/svg/UserCircle.svg'
import { ReactComponent as ClearIcon } from '../../../common/images/svg/XCircle.svg'
import { CustomButton } from '../../../components/button/buttonCustom'
import { useNotification } from '../../../components/providers/notification/useNotification'
import { ButtonTransferMode } from '../../../pages/home/component/button-transfer-mode'
import { clearFieldErrorMessageOnChange } from '../../../utils/form.constants'
import { socketDisconnect } from '../../../utils/socket'
import './userDetails.css'

const requiredFields = ['fromDate', 'toDate']

const listTimeStatement = { THIS_MONTH: 'this_month', LAST_MONTH: 'last_month' }

const UserDetails = ({
  setOpenUserDetails,
  infoProfile,
  avatar,
}: {
  setOpenUserDetails: React.Dispatch<React.SetStateAction<boolean>>
  infoProfile: any
  avatar: string
}) => {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const { open: openNotification } = useNotification()

  const fromDatePickerRef = useRef<any>(null)
  const toDatePickerRef = useRef<any>(null)

  const [status, setStatus] = useState<any>(null)
  const [copied, setCopied] = useState(false)
  const [showModalStatement, setShowModalStatement] = useState(false)
  const [isAllFieldsFilled, setIsAllFieldsFilled] = useState(false)
  const [searchFromDate, setSearchFromDate] = useState<any>()
  const [searchToDate, setSearchToDate] = useState<any>()
  const [timeStatement, setTimeStatement] = useState<any>(
    listTimeStatement.THIS_MONTH
  )
  const [showAllBalances, setShowAllBalances] = useState(false)

  const checkAllFieldsFilled = () => {
    const values = form.getFieldsValue(requiredFields)
    return requiredFields.every((field) => !!values[field])
  }

  const { mutate: logout } = useLogout({
    onSuccess: () => {
      socketDisconnect()
      localStorage.clear()
      window.location.href = '/app/login'
    },
  })

  const { mutate: printStatementReport, isLoading } = usePrintStatementReport({
    onSuccess: (response: { data: { data: { url: string } } }) => {
      setShowModalStatement(false)
      setTimeStatement(listTimeStatement.THIS_MONTH)
      setShowAllBalances(false)
      form.resetFields()
      setSearchFromDate(null)
      setSearchToDate(null)
      window.open(response.data.data.url, '_blank')
    },
    onError: (error: { message: string }) => {
      if (openNotification) {
        openNotification?.({
          key: 'print_fail',
          message: error.message || 'Something went wrong!',
          type: 'error',
        })
      }
    },
  })

  const onLogout = useCallback(() => {
    logout()
  }, [logout])

  const onProfile = () => {
    navigate('/profile')
    setOpenUserDetails(false)
  }

  const onChangePassword = () => {
    navigate('/change-password')
    setOpenUserDetails(false)
  }

  const onShowModalStatement = () => {
    setShowModalStatement(true)
    setOpenUserDetails(false)
  }

  const handleCancelStatement = () => {
    if (isLoading) return
    setShowModalStatement(false)
    setTimeStatement(listTimeStatement.THIS_MONTH)
    setShowAllBalances(false)
    form.resetFields()
    setSearchFromDate(null)
    setSearchToDate(null)
  }

  const handleSearchFromDate = (
    _date: dayjs.Dayjs | null,
    dateString: string | string[]
  ) => {
    setSearchFromDate(Array.isArray(dateString) ? dateString[0] : dateString)
  }

  const handleSearchToDate = (
    _date: dayjs.Dayjs | null,
    dateString: string | string[]
  ) => {
    setSearchToDate(Array.isArray(dateString) ? dateString[0] : dateString)
  }

  const onSubmitStatement = (value: any) => {
    const body = {
      startDate: dayjs(value?.fromDate).endOf('day').toISOString(),
      endDate: dayjs(value?.toDate).endOf('day').toISOString(),
      showAllBalances,
    }
    printStatementReport(body)
  }

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(infoProfile.data.data.smartUID)
    setCopied(true)
    if (openNotification) {
      openNotification?.({
        key: 'add_bank_success',
        message: 'Copied SMART UID successfully!',
        type: 'success',
      })
    }
  }

  useEffect(() => {
    if (!infoProfile) return
    setStatus(infoProfile.data.data?.status)
  }, [infoProfile])

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => {
        setCopied(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [copied])

  return (
    <div className={'user-details'}>
      <div className={'user-details-profile'}>
        <img className='avt-user-detail' alt='avt-user' src={avatar} />
        <div className='info-user'>
          <div className='name'>
            <Typography
              style={{ fontSize: 16, fontWeight: 700, color: 'white' }}>
              {infoProfile.data.data.fullName}
            </Typography>
            {status && (
              <div
                className='status'
                style={{ color: COLOR_STATUS_USER[status] }}>
                {STATUS_USER_ENUM[status]}
              </div>
            )}
          </div>
          <Typography
            style={{
              fontSize: 12,
              fontWeight: 400,
              color: 'rgba(255, 255, 255, 0.6)',
            }}>
            {infoProfile.data.data.email}
          </Typography>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '5px',
            }}>
            <Typography
              style={{ fontSize: 12, color: 'white', width: 'fit-content' }}>
              SMART UID: {infoProfile.data.data.smartUID}
            </Typography>
            {copied ? (
              <SuccessIcon style={{ width: 14, height: 14 }} />
            ) : (
              <img
                alt='copy'
                src={copyIcon}
                className='icon-animation'
                style={{ width: 16, height: 16, cursor: 'pointer' }}
                onClick={handleCopy}
              />
            )}
          </div>
        </div>
      </div>
      <Divider className={'user-details__divider'} />
      <div className={'user-details-content'} onClick={onProfile}>
        <UserCircleIcon />
        <Typography style={{ fontSize: 14, fontWeight: 400, color: 'white' }}>
          Profile
        </Typography>
      </div>
      <div className={'user-details-content'} onClick={onChangePassword}>
        <SignoutPasswordIcon />
        <Typography style={{ fontSize: 14, fontWeight: 400, color: 'white' }}>
          Change Password
        </Typography>
      </div>
      <div className={'user-details-content'} onClick={onShowModalStatement}>
        <StatementIcon />
        <Typography style={{ fontSize: 14, fontWeight: 400, color: 'white' }}>
          e-Statement
        </Typography>
      </div>
      <Divider className={'user-details__divider'} />
      <div className={'user-details-content'} onClick={onLogout}>
        <SignoutIcon />
        <Typography style={{ fontSize: 14, fontWeight: 400, color: '#EC3333' }}>
          Sign out
        </Typography>
      </div>
      <Modal
        open={showModalStatement}
        onCancel={handleCancelStatement}
        footer={null}
        destroyOnClose
        maskClosable={false}
        title={
          <>
            <Typography
              style={{ fontSize: 20, fontWeight: 700, color: '#C2C6CE' }}>
              {`Statements & Reports`}
            </Typography>
            <CloseIcon
              className='icon-animation'
              onClick={handleCancelStatement}
              style={{ cursor: 'pointer' }}
            />
          </>
        }
        className='send-money-modal'>
        <Form
          form={form}
          onFinish={onSubmitStatement}
          onValuesChange={() => {
            clearFieldErrorMessageOnChange(form)
            setIsAllFieldsFilled(checkAllFieldsFilled())
          }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              padding: '20px',
            }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
              }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: '12px',
                }}>
                <ButtonTransferMode
                  size={'medium'}
                  text='This month'
                  width={'108px'}
                  onClick={() => {
                    setTimeStatement(listTimeStatement.THIS_MONTH)
                    const startOfMonth = dayjs().startOf('month')
                    const endOfToday = dayjs().endOf('day')
                    form.setFieldsValue({
                      fromDate: startOfMonth,
                      toDate: endOfToday,
                    })
                    setSearchFromDate(startOfMonth.format('DD/MM/YYYY'))
                    setSearchToDate(endOfToday.format('DD/MM/YYYY'))
                    setIsAllFieldsFilled(true)
                  }}
                  disabled={isLoading}
                />
                <ButtonTransferMode
                  size={'medium'}
                  text='Last month'
                  width={'108px'}
                  onClick={() => {
                    setTimeStatement(listTimeStatement.LAST_MONTH)
                    const startOfLastMonth = dayjs()
                      .subtract(1, 'month')
                      .startOf('month')

                    const endOfLastMonth = dayjs()
                      .subtract(1, 'month')
                      .endOf('month')
                    form.setFieldsValue({
                      fromDate: startOfLastMonth,
                      toDate: endOfLastMonth,
                    })
                    setSearchFromDate(startOfLastMonth.format('DD/MM/YYYY'))
                    setSearchToDate(endOfLastMonth.format('DD/MM/YYYY'))
                    setIsAllFieldsFilled(true)
                  }}
                  disabled={isLoading}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                <Typography
                  style={{
                    fontSize: 14,
                    fontWeight: 400,
                    color: 'rgba(255, 255, 255, 0.85)',
                    whiteSpace: 'nowrap',
                  }}>
                  Show all balances
                </Typography>
                <Switch
                  checked={showAllBalances}
                  onChange={setShowAllBalances}
                  disabled={isLoading}
                />
              </div>
            </div>
            <Row gutter={{ xs: 8, sm: 10, md: 20 }}>
              <Col xs={24} md={12}>
                <Form.Item
                  style={{ width: '100%' }}
                  label='From Date'
                  name='fromDate'
                  validateTrigger='onBlur'
                  rules={[
                    { required: true, message: 'This field is required' },
                  ]}>
                  <DatePicker
                    ref={fromDatePickerRef}
                    placeholder='Select date'
                    className='custom-input-date statement-report'
                    format={'DD/MM/YYYY'}
                    style={{ height: '44px !important' }}
                    onChange={(
                      value: dayjs.Dayjs | null,
                      dateString: string | string[]
                    ) => {
                      handleSearchFromDate(value, dateString)
                      if (!value) {
                        setTimeout(() => {
                          fromDatePickerRef.current?.focus?.()
                        })
                      }
                    }}
                    clearIcon={<ClearIcon style={{ height: 12, width: 12 }} />}
                    suffixIcon={searchFromDate ? null : <CalenderIcon />}
                    allowClear
                    disabled={!timeStatement || isLoading}
                    disabledDate={(current) => {
                      if (!current) return false
                      // Disable future dates
                      if (current.isAfter(dayjs(), 'day')) return true
                      // Disable dates before createdAt
                      const createdAt = infoProfile?.data?.data?.createdAt || infoProfile?.data?.data?.created_at
                      if (createdAt) {
                        const createdAtDate = dayjs(createdAt)
                        if (current.isBefore(createdAtDate, 'day')) return true
                      }
                      const toDate = form.getFieldValue('toDate')
                      if (toDate) {
                        const toDateValue = dayjs(toDate)
                        // Disable dates after toDate
                        if (current.isAfter(toDateValue, 'day')) return true
                        // Disable dates more than 6 months before toDate
                        const sixMonthsBeforeToDate = toDateValue.subtract(6, 'month')
                        if (current.isBefore(sixMonthsBeforeToDate, 'day')) return true
                      }
                      return false
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  style={{ width: '100%' }}
                  label='To Date'
                  name='toDate'
                  validateTrigger='onBlur'
                  rules={[
                    { required: true, message: 'This field is required' },
                  ]}>
                  <DatePicker
                    ref={toDatePickerRef}
                    placeholder='Select date'
                    className='custom-input-date statement-report'
                    format={'DD/MM/YYYY'}
                    onChange={(
                      value: dayjs.Dayjs | null,
                      dateString: string | string[]
                    ) => {
                      handleSearchToDate(value, dateString)
                      if (!value) {
                        setTimeout(() => {
                          toDatePickerRef.current?.focus?.()
                        })
                      }
                    }}
                    clearIcon={<ClearIcon style={{ height: 12, width: 12 }} />}
                    suffixIcon={searchToDate ? null : <CalenderIcon />}
                    allowClear
                    disabled={!timeStatement || isLoading}
                    disabledDate={(current) => {
                      if (!current) return false
                      // Disable future dates
                      if (current.isAfter(dayjs(), 'day')) return true
                      // Disable dates before createdAt
                      const createdAt = infoProfile?.data?.data?.createdAt || infoProfile?.data?.data?.created_at
                      if (createdAt) {
                        const createdAtDate = dayjs(createdAt)
                        if (current.isBefore(createdAtDate, 'day')) return true
                      }
                      const fromDate = form.getFieldValue('fromDate')
                      if (fromDate) {
                        const fromDateValue = dayjs(fromDate)
                        // Disable dates before fromDate
                        if (current.isBefore(fromDateValue, 'day')) return true
                        // Disable dates more than 6 months after fromDate
                        const sixMonthsAfterFromDate = fromDateValue.add(6, 'month')
                        if (current.isAfter(sixMonthsAfterFromDate, 'day')) return true
                      }
                      return false
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <CustomButton
              type='submit'
              text={'View'}
              size={'large'}
              width={'100%'}
              isLoading={isLoading}
              disabled={!isAllFieldsFilled}
            />
          </div>
        </Form>
      </Modal>
    </div>
  )
}
export default UserDetails
